import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { APP_STATE } from '../../../constants'
import Loader from '../../shared/Loader'

import ModalWindow from '../ModalWindow'
import TooltipIcon from '../TooltipIcon'
import DeselectableDropdown from '../DeselectableDropdown'
import Input from '../Input'
import Text from '../Text'
import {
  VERMILION,
  RED,
  TEXT_SMALL,
  TEXT_MEDIUM,
  TEXT_BIG,
} from '../../../constants'
import RuleSelectionSection from '../FeedAuditorComponents/RuleSelectionSection'

const Grid = styled.div`
  display: grid;
  grid-gap: 18px;
`

const Line = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
`

const ErrorMessage = styled.div`
  position: absolute;
  size: ${TEXT_SMALL};
  color: ${VERMILION};
  bottom: -20px;
  left: 5px;
`

const RulesetCreationDialog = ({
  onCancel,
  onSave,
  visible,
  rulesetList,
  rulesList,
  auditModalRulesState,
  auditModalRulesetsState,
  rulesetCreationState,
  rulesetCreationResponse,
}) => {
  const { t } = useTranslation()
  const [newRulesetName, setNewRulesetName] = useState(null)
  const [selectedRuleset, setSelectedRuleset] = useState('')
  const [selectedRulesList, setSelectedRulesList] = useState([])
  const [formErrors, setFormErrors] = useState({})

  useEffect(
    () => {
      setFormErrors({
        noName: newRulesetName == '',
        noRules: selectedRulesList.length === 0,
      })
    },
    [newRulesetName, selectedRuleset, selectedRulesList]
  )

  useEffect(
    () => {
      // Reset dialog when creation is successful
      if (rulesetCreationState == APP_STATE.DONE) {
        setStateToInitial()
      }
    },
    [rulesetCreationState]
  )

  const handleClose = () => {
    onCancel()
    setStateToInitial()
  }

  const setStateToInitial = () => {
    setNewRulesetName(null)
    setSelectedRulesList([])
  }

  const handleSave = () => {
    onSave(newRulesetName, selectedRulesList)
  }

  const rulesetChanged = newRuleset => {
    if (newRuleset === '') {
      return
    }
    setSelectedRuleset(newRuleset.id)

    setSelectedRulesList(newRuleset.rules)
  }

  function isFormValid() {
    let formIsValid = true

    if (newRulesetName === '' || newRulesetName === null) {
      formIsValid = false
    }

    if (selectedRuleset !== '') {
      formIsValid = false
    }

    if (selectedRulesList.length === 0) {
      formIsValid = false
    }

    return formIsValid
  }

  function isModalDataFetching() {
    //if states are undefined, null, or FETCHING return true
    if (
      !auditModalRulesState ||
      auditModalRulesState == APP_STATE.FETCHING ||
      !auditModalRulesetsState ||
      auditModalRulesetsState == APP_STATE.FETCHING
    ) {
      return true
    }
    return false
  }

  function isModalDataFailed() {
    if (
      auditModalRulesState == APP_STATE.ERROR ||
      auditModalRulesetsState == APP_STATE.ERROR
    ) {
      return true
    }
    return false
  }

  return (
    <ModalWindow
      minWidth={'40%'}
      setWidth={'50%'}
      okText={t('create_ruleset')}
      onCancel={handleClose}
      onOk={handleSave}
      title={t('create_ruleset_title')}
      visible={visible}
      okButtonDisabledState={!isFormValid()}
    >
      {isModalDataFetching() ? (
        <Loader />
      ) : isModalDataFailed() ? (
        t('feed_audit_modal_data_failed')
      ) : (
        <Grid>
          <Line>
            <Text size={TEXT_BIG} color={RED} bold>
              {t('ruleset_creation_warning1')}
            </Text>
            <TooltipIcon
              title={t('ruleset_creation_warning2')}
              position={'bottom'}
              colour={RED}
            />
          </Line>
          {rulesetCreationState == APP_STATE.ERROR && (
            <Line>
              <Text size={TEXT_BIG} color={RED} bold>
                {rulesetCreationResponse}
              </Text>
            </Line>
          )}
          <Line>
            <Input
              name="rulesetName"
              type="text"
              label={t('ruleset_creation_name')}
              onChange={e => setNewRulesetName(e.target.value)}
              value={newRulesetName}
              setWidth={'-webkit-fill-available'}
            />
            {formErrors.noName ? (
              <ErrorMessage>{t('ruleset_creation_name_required')}</ErrorMessage>
            ) : null}
          </Line>
          <Line>
            <DeselectableDropdown
              label={t('feed_audit_ruleset')}
              placeholder={t('ruleset_creation_placeholder')}
              dataId="audit-ruleset"
              name="auditRuleset"
              options={rulesetList}
              boundValue={rulesetList.find(r => r.id === selectedRuleset)}
              onChange={r => rulesetChanged(r)}
            />
            <TooltipIcon
              title={t('ruleset_creation_dropdown_tooltip')}
              position={'left'}
            />
            {formErrors.noRules ? (
              <ErrorMessage>
                {t('ruleset_creation_rules_required')}
              </ErrorMessage>
            ) : null}
          </Line>
          <span />
          {rulesList.map(group => (
            <RuleSelectionSection
              ruleGroupName={group.name}
              displayReportType={0}
              rulesList={group.rule}
              selectedRulesList={selectedRulesList}
              setSelectedRulesList={setSelectedRulesList}
              resetSelectedRuleset={() => setSelectedRuleset('')}
            />
          ))}
        </Grid>
      )}
    </ModalWindow>
  )
}

export default RulesetCreationDialog
